import { createEffect, createSignal, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import { type ChatActions, type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";

export interface ArtifactStatus {
  // "missing" is client-side: the server left the artifact out of its answer,
  // or sent a status this client doesn't know.
  status: "pending" | "ready" | "failed" | "missing";
  url?: string;
}

interface WaitedArtifact {
  artifactId: string;
  storageLocation: MediaStorageLocation;
}

type ArtifactActions = Pick<ChatActions, "resolveImageUrls" | "subscribeArtifactChanges">;
type ResolveUrls = NonNullable<ChatActions["resolveImageUrls"]>;
type ResolvedUrl = Awaited<ReturnType<ResolveUrls>>[number];

// Every pending card waits on one shared poll. A host that pushes change events
// gets a slow fallback poll, since events can be missed. Without events the poll
// starts fast and backs off, as most renders take a minute or two. A card stops
// waiting after the render step's own limit.
const POLL_MS = 2_000;
const POLL_SLOW_AFTER_MS = 30_000;
const POLL_SLOW_MS = 5_000;
const POLL_FALLBACK_MS = 60_000;
const POLL_MAX_MS = 15 * 60_000;

const storageKey = (loc: MediaStorageLocation) => `${loc.bucket}/${loc.key}`;

const statuses = new Map<string, ArtifactStatus>();
// Mounted pending cards per artifact id, so the poll stops once no card is left waiting.
const waiting = new Map<string, { location: MediaStorageLocation; cards: number }>();
let polling = false;
let queued: Map<string, MediaStorageLocation> | undefined;
const [version, setVersion] = createSignal(0);

const isSettled = (status: ArtifactStatus | undefined) => status?.status === "ready" || status?.status === "failed";

const unsettled = (): MediaStorageLocation[] => {
  const locations: MediaStorageLocation[] = [];
  for (const { location } of waiting.values()) {
    if (!isSettled(statuses.get(storageKey(location)))) locations.push(location);
  }
  return locations;
};

export const toArtifactStatus = (resolved: ResolvedUrl | undefined): ArtifactStatus => {
  if (!resolved) return { status: "missing" };
  // Sync artifacts (images, charts) are ready on creation and carry no status.
  const status = resolved.status ?? "ready";
  if (status === "ready") return resolved.url ? { status, url: resolved.url } : { status };
  if (status === "pending" || status === "failed") return { status };
  return { status: "missing" };
};

async function fetchStatuses(resolve: ResolveUrls, locations: MediaStorageLocation[]): Promise<void> {
  const resolved = new Map((await resolve(locations)).map((r) => [storageKey(r), r]));
  for (const loc of locations) {
    statuses.set(storageKey(loc), toArtifactStatus(resolved.get(storageKey(loc))));
  }
  setVersion((v) => v + 1);
}

// A failed lookup says nothing about the render; the next tick asks again.
const fetchQuietly = (resolve: ResolveUrls, locations: MediaStorageLocation[]) =>
  fetchStatuses(resolve, locations).catch(() => {});

// Opening a chat mounts all its pending cards in one pass; they share one request.
function fetchSoon(resolve: ResolveUrls, location: MediaStorageLocation): void {
  if (queued) {
    queued.set(storageKey(location), location);
    return;
  }
  const batch = new Map([[storageKey(location), location]]);
  queued = batch;
  queueMicrotask(() => {
    queued = undefined;
    void fetchQuietly(resolve, [...batch.values()]);
  });
}

function poll({ resolveImageUrls: resolve, subscribeArtifactChanges }: ArtifactActions): void {
  if (!resolve || polling) return;
  polling = true;
  const startedAt = Date.now();
  const unsubscribe = subscribeArtifactChanges?.((artifactId) => {
    const location = waiting.get(artifactId)?.location;
    if (location && !isSettled(statuses.get(storageKey(location)))) fetchSoon(resolve, location);
  });
  const interval = () => {
    if (unsubscribe) return POLL_FALLBACK_MS;
    return Date.now() - startedAt > POLL_SLOW_AFTER_MS ? POLL_SLOW_MS : POLL_MS;
  };
  const tick = () => {
    const locations = unsettled();
    if (locations.length === 0) {
      polling = false;
      unsubscribe?.();
      return;
    }
    void fetchQuietly(resolve, locations).then(() => setTimeout(tick, interval()));
  };
  setTimeout(tick, interval());
}

/**
 * Keeps a mounted pending card's artifact in the shared poll until the returned
 * release is called.
 */
export function waitForArtifact(actions: ArtifactActions, { artifactId, storageLocation }: WaitedArtifact): () => void {
  const key = storageKey(storageLocation);
  waiting.set(artifactId, { location: storageLocation, cards: (waiting.get(artifactId)?.cards ?? 0) + 1 });
  if (actions.resolveImageUrls && !isSettled(statuses.get(key))) fetchSoon(actions.resolveImageUrls, storageLocation);
  poll(actions);
  return () => {
    const entry = waiting.get(artifactId);
    if (entry && entry.cards > 1) {
      entry.cards -= 1;
      return;
    }
    waiting.delete(artifactId);
    // A signed URL expires, so it only outlives the artifact's cards as a bare
    // status; the next card to mount signs a fresh one.
    const known = statuses.get(key);
    if (known?.url) statuses.set(key, { status: known.status });
  };
}

/**
 * Live status of an async artifact (a rendering video), resolved through the
 * runtime's signed-URL lookup while `artifact` is set. `capped` turns true once
 * this card has waited the render step's full limit without a settled status;
 * it then stops polling.
 */
export function useArtifactStatus(artifact: () => WaitedArtifact | undefined) {
  const rt = useChatRuntime();
  const [capped, setCapped] = createSignal(false);

  createEffect(() => {
    const waited = artifact();
    if (isServer || !waited || capped()) return;
    const release = waitForArtifact(rt.actions, waited);
    const timer = setTimeout(() => setCapped(true), POLL_MAX_MS);
    onCleanup(() => {
      clearTimeout(timer);
      release();
    });
  });

  return {
    status: () => {
      const waited = artifact();
      if (!waited) return undefined;
      // Finished videos never wait, so only pending cards subscribe to the shared poll's ticks.
      version();
      return statuses.get(storageKey(waited.storageLocation));
    },
    capped,
  };
}
